import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useGroupTree,
  useImportFeideGroup,
  useMapFeideGroup,
  useFeideGroups
} from "../../../api/hooks";
import authAPI from "../../../api/authAPI";
import Modal from "../../_shared/Modal/Modal";
import Button from "../../_shared/Button/Button";
import styles from "./Modal.module.css";

const ImportOrMapFeideGroupModal = ({ isOpen, onClose, feideGroup, organizationId, onSuccess }) => {
  const { t } = useTranslation("common");
  const [selectedDigupGroupId, setSelectedDigupGroupId] = useState("");

  const { data: treeData } = useGroupTree(organizationId, false, {
    enabled: !!organizationId && isOpen
  });
  const { data: feideGroupsData } = useFeideGroups(organizationId, false, {
    enabled: !!organizationId && isOpen
  });
  const importMutation = useImportFeideGroup();
  const mapMutation = useMapFeideGroup();

  const allGroups = treeData?.data || [];
  const feideGroups = feideGroupsData?.data || [];
  const isSubmitting = importMutation.isLoading || mapMutation.isLoading;

  // Filter out groups that are already linked to any FEIDE group
  const availableGroups = useMemo(() => {
    const linkedDigupGroupIds = new Set();
    feideGroups.forEach(fg => {
      if (fg.mappings && fg.mappings.length > 0) {
        fg.mappings.forEach(mapping => {
          linkedDigupGroupIds.add(mapping.digupGroupId);
        });
      }
    });
    return allGroups.filter(group => !linkedDigupGroupIds.has(group.id));
  }, [allGroups, feideGroups]);

  useEffect(() => {
    if (isOpen) {
      setSelectedDigupGroupId("");
    }
  }, [isOpen]);

  const importGroupWithParents = async (feideGroupId, includeMembersFlag) => {
    // Fetch current state of groups to check import status
    const groupsResponse = await authAPI.getFeideGroupsForOrganization(parseInt(organizationId), false);
    const currentGroups = groupsResponse.data || [];
    const currentFeideGroup = currentGroups.find(g => g.id === feideGroupId);

    if (!currentFeideGroup) {
      throw new Error("FEIDE group not found");
    }

    let parentDigupGroupId = parseInt(organizationId);

    // If this group has a parent FEIDE group, ensure parent is imported first
    if (currentFeideGroup.parentFeideGroupId) {
      const parentGroup = currentGroups.find(g => g.id === currentFeideGroup.parentFeideGroupId);

      if (parentGroup) {
        // Check if parent is already imported
        if (!parentGroup.mappings || parentGroup.mappings.length === 0) {
          // Parent not imported yet, import it first (without members to avoid duplicates)
          await importGroupWithParents(parentGroup.id, false);

          // Fetch updated state to get parent's mapping
          const updatedResponse = await authAPI.getFeideGroupsForOrganization(parseInt(organizationId), false);
          const updatedGroups = updatedResponse.data || [];
          const updatedParent = updatedGroups.find(g => g.id === currentFeideGroup.parentFeideGroupId);

          if (updatedParent && updatedParent.mappings && updatedParent.mappings.length > 0) {
            parentDigupGroupId = updatedParent.mappings[0].digupGroupId;
          }
        } else {
          // Parent already imported, use its Digup group ID
          parentDigupGroupId = parentGroup.mappings[0].digupGroupId;
        }
      }
    }

    // Import current group with correct parent using mutation
    await importMutation.mutateAsync({
      feideGroupId,
      organizationId: parseInt(organizationId),
      parentGroupId: parentDigupGroupId,
      includeMembers: includeMembersFlag
    });
  };

  const handleImport = async () => {
    if (!feideGroup) return;

    try {
      await importGroupWithParents(feideGroup.id, true);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Import failed:", err);
    }
  };

  const handleMap = async () => {
    if (!feideGroup || !selectedDigupGroupId) return;

    try {
      await mapMutation.mutateAsync({
        feideGroupId: feideGroup.id,
        organizationId: parseInt(organizationId),
        digupGroupId: parseInt(selectedDigupGroupId)
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Map failed:", err);
    }
  };

  if (!feideGroup) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("auth.settings.feideGroups.manageTitle", { name: feideGroup.name })}
    >
      <div className={styles.modalContent}>
        {/* Import as new group */}
        <div className={styles.section}>
          <h4>{t("auth.settings.feideGroups.import.button")}</h4>
          <p>{t("auth.settings.feideGroups.import.description")}</p>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={isSubmitting}
          >
            {importMutation.isLoading
              ? t("auth.settings.feideGroups.import.importing")
              : t("auth.settings.feideGroups.import.button")}
          </Button>
        </div>

        <hr className={styles.divider} />

        {/* Map to existing group */}
        <div className={styles.section}>
          <h4>{t("auth.settings.feideGroups.actions.map")}</h4>
          <p>{t("auth.settings.feideGroups.map.description")}</p>
          <div className={styles.inputGroup}>
            <label>{t("auth.settings.feideGroups.map.selectGroup")}</label>
            <select
              className={styles.select}
              value={selectedDigupGroupId}
              onChange={(e) => setSelectedDigupGroupId(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="">{t("auth.settings.feideGroups.map.selectPlaceholder")}</option>
              {availableGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={handleMap}
            disabled={isSubmitting || !selectedDigupGroupId}
          >
            {mapMutation.isLoading
              ? t("auth.settings.feideGroups.map.mapping")
              : t("auth.settings.feideGroups.map.button")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportOrMapFeideGroupModal;
